import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useTransactions } from '../contexts/TransactionContext';
import { useBudgets } from '../contexts/BudgetContext';
import { useTheme } from '../contexts/ThemeContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { TrendingUp, TrendingDown, DollarSign, Plus, Edit2, Trash2, Sparkles, Flame } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { calculateBudgetIntelligence, BUDGET_STATUS } from 'shared/utils/budgetIntelligence';
import BudgetAlertsBanner from '../components/BudgetAlertsBanner';

const Stats = () => {
  const navigate = useNavigate();
  const { allTransactions, selectedMonth } = useTransactions();
  const { budgets, setBudget, removeBudget, loading: budgetsLoading } = useBudgets();
  const { theme, isDark } = useTheme();
  const { formatCurrency, displayCurrency, getCurrencyInfo } = useCurrency();
  const currencyInfo = getCurrencyInfo(displayCurrency);
  const [showBudgetDialog, setShowBudgetDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [budgetAmount, setBudgetAmount] = useState('');

  // Dynamic colors for dark/light mode
  const COLORS = isDark 
    ? ['#FF6B6B', '#64B5F6', '#81C784', '#FFB74D', '#BA68C8', '#4DD0E1', '#FFD54F', '#F06292', '#7986CB', '#AED581']
    : ['#E8544F', '#5B9FE3', '#4CAF50', '#FF9800', '#9C27B0', '#00BCD4', '#FFC107', '#E91E63', '#3F51B5', '#8BC34A'];

  // Filter transactions for current month
  const currentMonthTransactions = useMemo(() => {
    const monthStart = startOfMonth(selectedMonth);
    const monthEnd = endOfMonth(selectedMonth);
    return allTransactions.filter(t => {
      const transactionDate = t.date;
      return transactionDate >= monthStart && transactionDate <= monthEnd;
    });
  }, [allTransactions, selectedMonth]);

  // Calculate category-wise expense breakdown for pie chart
  const categoryData = useMemo(() => {
    const expenses = currentMonthTransactions.filter(t => t.type === 'expense');
    const categoryTotals = {};
    
    expenses.forEach(t => {
      if (!categoryTotals[t.category]) {
        categoryTotals[t.category] = 0;
      }
      categoryTotals[t.category] += t.amount;
    });

    return Object.entries(categoryTotals)
      .map(([category, amount]) => ({ category, amount }))
      .sort((a, b) => b.amount - a.amount);
  }, [currentMonthTransactions]);

  // Calculate monthly spending for last 6 months for bar chart
  const monthlyData = useMemo(() => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const month = subMonths(new Date(), i);
      const monthStart = startOfMonth(month);
      const monthEnd = endOfMonth(month);
      
      const monthTransactions = allTransactions.filter(t => {
        const transactionDate = t.date;
        return transactionDate >= monthStart && transactionDate <= monthEnd;
      });

      const income = monthTransactions
        .filter(t => t.type === 'income')
        .reduce((sum, t) => sum + t.amount, 0);
      
      const expense = monthTransactions
        .filter(t => t.type === 'expense')
        .reduce((sum, t) => sum + t.amount, 0);

      months.push({
        month: format(month, 'MMM'),
        income,
        expense
      });
    }
    return months;
  }, [allTransactions]);

  // Smart Budget Intelligence with burn rate and predictions (using user's selected currency)
  const budgetIntelligence = useMemo(() => {
    return calculateBudgetIntelligence(budgets, allTransactions, selectedMonth, formatCurrency);
  }, [budgets, allTransactions, selectedMonth, formatCurrency]);

  const categoryBudgetData = budgetIntelligence.categories;

  const handleSetBudget = async () => {
    if (!selectedCategory || !budgetAmount || parseFloat(budgetAmount) <= 0) {
      toast.error('Please enter a valid budget amount');
      return;
    }

    try {
      await setBudget(selectedCategory, budgetAmount);
      toast.success(`Budget set for ${selectedCategory}`);
      setShowBudgetDialog(false);
      setSelectedCategory(null);
      setBudgetAmount('');
    } catch (error) {
      console.error('Failed to set budget:', error);
      toast.error('Failed to set budget');
    }
  };

  const handleRemoveBudget = async (category) => {
    try {
      await removeBudget(category);
      toast.success(`Budget removed for ${category}`);
    } catch (error) {
      console.error('Failed to remove budget:', error);
      toast.error('Failed to remove budget');
    }
  };

  const totalExpense = categoryData.reduce((sum, item) => sum + item.amount, 0);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        {/* Header with AI Insights Button */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold" style={{ color: theme.text }}>Statistics & Budgets</h1>
          <Button
            onClick={() => navigate('/insights')}
            className="flex items-center gap-2"
            style={{ backgroundColor: theme.accent, color: 'white' }}
            data-testid="ai-insights-btn"
          >
            <Sparkles className="w-4 h-4" />
            AI Insights
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 rounded-lg" style={{ backgroundColor: theme.cardBackground, boxShadow: theme.shadow }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm" style={{ color: theme.textSecondary }}>Total Spent</p>
                <p className="text-2xl font-bold" style={{ color: theme.expense }}>
                  {formatCurrency(totalExpense)}
                </p>
              </div>
              <TrendingDown className="w-8 h-8" style={{ color: theme.expense }} />
            </div>
          </div>

          <div className="p-4 rounded-lg" style={{ backgroundColor: theme.cardBackground, boxShadow: theme.shadow }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm" style={{ color: theme.textSecondary }}>Categories</p>
                <p className="text-2xl font-bold" style={{ color: theme.text }}>
                  {categoryData.length}
                </p>
              </div>
              <DollarSign className="w-8 h-8" style={{ color: theme.textSecondary }} />
            </div>
          </div>

          <div className="p-4 rounded-lg" style={{ backgroundColor: theme.cardBackground, boxShadow: theme.shadow }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm" style={{ color: theme.textSecondary }}>Avg per Day</p>
                <p className="text-2xl font-bold" style={{ color: theme.text }}>
                  {formatCurrency(totalExpense / 30)}
                </p>
              </div>
              <TrendingUp className="w-8 h-8" style={{ color: theme.income }} />
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Pie Chart */}
          <div className="p-6 rounded-lg" style={{ backgroundColor: theme.cardBackground, boxShadow: theme.shadow }}>
            <h2 className="text-lg font-semibold mb-4" style={{ color: theme.text }}>Expense by Category</h2>
            {categoryData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ category, percent }) => `${category} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="amount"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)} 
                    contentStyle={{ 
                      backgroundColor: theme.cardBackground, 
                      border: `1px solid ${theme.divider}`,
                      borderRadius: '8px',
                      color: theme.text
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center" style={{ color: theme.textSecondary }}>
                No expense data for this month
              </div>
            )}
          </div>

          {/* Bar Chart */}
          <div className="p-6 rounded-lg" style={{ backgroundColor: theme.cardBackground, boxShadow: theme.shadow }}>
            <h2 className="text-lg font-semibold mb-4" style={{ color: theme.text }}>Last 6 Months</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.divider} />
                <XAxis dataKey="month" tick={{ fill: theme.textSecondary }} />
                <YAxis tick={{ fill: theme.textSecondary }} />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)} 
                  contentStyle={{ 
                    backgroundColor: theme.cardBackground, 
                    border: `1px solid ${theme.divider}`,
                    borderRadius: '8px',
                    color: theme.text
                  }}
                />
                <Legend wrapperStyle={{ color: theme.text }} />
                <Bar dataKey="income" fill={theme.accentBlue} name="Income" />
                <Bar dataKey="expense" fill={theme.accent} name="Expense" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Smart Predictive Budget Alerts Banner */}
        <BudgetAlertsBanner budgetData={budgetIntelligence} />

        {/* Budget Tracking */}
        <div className="p-6 rounded-lg mb-6" style={{ backgroundColor: theme.cardBackground, boxShadow: theme.shadow }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold" style={{ color: theme.text }}>Budget Tracking & Forecasts</h2>
              <p className="text-xs" style={{ color: theme.textSecondary }}>
                Daily burn rate and month-end overspending predictions
              </p>
            </div>
            <Button
              onClick={() => {
                setSelectedCategory(null);
                setBudgetAmount('');
                setShowBudgetDialog(true);
              }}
              className="flex items-center gap-2"
              style={{ backgroundColor: theme.accent, color: 'white' }}
            >
              <Plus className="w-4 h-4" />
              Set Budget
            </Button>
          </div>

          <div className="space-y-4">
            {categoryBudgetData.map((item) => {
              const { category, spent, budgetLimit: budget, hasBudget, percentage, dailyBurnRate, projectedTotal, status, badgeColor, statusTitle } = item;
              const isOver = status === BUDGET_STATUS.OVERBUDGET;
              const isWarning = status === BUDGET_STATUS.WARNING;
              
              return (
                <div key={category} className="pb-4" style={{ borderBottom: `1px solid ${theme.divider}` }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium" style={{ color: theme.text }}>{category}</span>
                          {hasBudget && (
                            <span
                              className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                              style={{ backgroundColor: `${badgeColor}20`, color: badgeColor }}
                            >
                              {statusTitle}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold" style={{ color: isOver ? theme.accent : theme.text }}>
                            {formatCurrency(spent)}
                            {hasBudget && <span className="font-normal" style={{ color: theme.textSecondary }}> / {formatCurrency(budget)}</span>}
                          </span>
                          {hasBudget && (
                            <div className="flex gap-1">
                              <button
                                onClick={() => {
                                  setSelectedCategory(category);
                                  setBudgetAmount(budget.toString());
                                  setShowBudgetDialog(true);
                                }}
                                className="p-1 rounded transition-colors"
                                style={{ ':hover': { backgroundColor: theme.hover } }}
                                title="Edit budget"
                              >
                                <Edit2 className="w-4 h-4" style={{ color: theme.textSecondary }} />
                              </button>
                              <button
                                onClick={() => handleRemoveBudget(category)}
                                className="p-1 rounded transition-colors"
                                title="Remove budget"
                              >
                                <Trash2 className="w-4 h-4" style={{ color: theme.accent }} />
                              </button>
                            </div>
                          )}
                          {!hasBudget && (
                            <button
                              onClick={() => {
                                setSelectedCategory(category);
                                setBudgetAmount('');
                                setShowBudgetDialog(true);
                              }}
                              className="text-xs font-semibold px-2 py-1 rounded" 
                              style={{ backgroundColor: `${theme.accent}15`, color: theme.accent }}
                            >
                              Set Limit
                            </button>
                          )}
                        </div>
                      </div>
                      {hasBudget && (
                        <div className="mt-2">
                          <div className="w-full rounded-full h-2" style={{ backgroundColor: isDark ? '#252C38' : '#E9ECEF' }}>
                            <div
                              className="h-2 rounded-full transition-all duration-300"
                              style={{
                                width: `${Math.min(percentage, 100)}%`,
                                backgroundColor: badgeColor,
                              }}
                            />
                          </div>
                          <div className="flex items-center justify-between text-xs mt-1.5" style={{ color: theme.textSecondary }}>
                            <span style={{ color: badgeColor, fontWeight: '600' }}>
                              {percentage.toFixed(0)}% used
                            </span>
                            <div className="flex items-center gap-1">
                              <Flame className="w-3 h-3 text-orange-400" />
                              <span>Pace: {formatCurrency(dailyBurnRate)}/day</span>
                              <span className="mx-1">•</span>
                              <span>Est. Month End: <strong style={{ color: isOver || isWarning ? badgeColor : theme.text }}>{formatCurrency(projectedTotal)}</strong></span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {categoryBudgetData.length === 0 && (
            <div className="text-center py-8" style={{ color: theme.textSecondary }}>
              No spending data available. Start adding transactions!
            </div>
          )}
        </div>

        {/* Top Spending Categories */}
        <div className="p-6 rounded-lg" style={{ backgroundColor: theme.cardBackground, boxShadow: theme.shadow }}>
          <h2 className="text-lg font-semibold mb-4" style={{ color: theme.text }}>Top Spending Categories</h2>
          <div className="space-y-3">
            {categoryData.slice(0, 5).map((item, index) => (
              <div key={item.category} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  >
                    {index + 1}
                  </div>
                  <span className="font-medium" style={{ color: theme.text }}>{item.category}</span>
                </div>
                <div className="text-right">
                  <p className="font-bold" style={{ color: theme.accent }}>{formatCurrency(item.amount)}</p>
                  <p className="text-xs" style={{ color: theme.textSecondary }}>
                    {((item.amount / totalExpense) * 100).toFixed(1)}% of total
                  </p>
                </div>
              </div>
            ))}
          </div>

          {categoryData.length === 0 && (
            <div className="text-center py-8" style={{ color: theme.textSecondary }}>
              No expense data for this month
            </div>
          )}
        </div>

        {/* Set Budget Dialog */}
        <Dialog open={showBudgetDialog} onOpenChange={setShowBudgetDialog}>
          <DialogContent style={{ backgroundColor: theme.cardBackground, borderColor: theme.divider }}>
            <DialogHeader>
              <DialogTitle style={{ color: theme.text }}>{selectedCategory ? 'Edit Budget' : 'Set Budget'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {!selectedCategory && (
                <div>
                  <Label style={{ color: theme.textSecondary }}>Category</Label>
                  <select
                    value={selectedCategory || ''}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full mt-1 p-2 rounded-lg"
                    style={{ 
                      backgroundColor: isDark ? theme.inputBackground : '#FFFFFF',
                      borderColor: theme.divider,
                      color: theme.text,
                      border: `1px solid ${theme.divider}`
                    }}
                  >
                    <option value="">Select a category</option>
                    {categoryBudgetData.map(item => (
                      <option key={item.category} value={item.category}>{item.category}</option>
                    ))}
                  </select>
                </div>
              )}
              {selectedCategory && (
                <div>
                  <Label style={{ color: theme.textSecondary }}>Category: <strong style={{ color: theme.text }}>{selectedCategory}</strong></Label>
                </div>
              )}
              <div>
                <Label style={{ color: theme.textSecondary }}>Monthly Budget ({currencyInfo.symbol})</Label>
                <Input
                  type="number"
                  placeholder="Enter amount"
                  value={budgetAmount}
                  onChange={(e) => setBudgetAmount(e.target.value)}
                  className="mt-1"
                  style={{ 
                    backgroundColor: isDark ? theme.inputBackground : '#FFFFFF',
                    borderColor: theme.divider,
                    color: theme.text
                  }}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBudgetDialog(false)} style={{ borderColor: theme.divider, color: theme.text }}>
                Cancel
              </Button>
              <Button
                onClick={handleSetBudget}
                style={{ backgroundColor: theme.accent, color: 'white' }}
              >
                {selectedCategory && budgets[selectedCategory] ? 'Update' : 'Set'} Budget
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Stats;
